import React, { useState, useEffect } from 'react';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Chip,
  Box,
  TextField,
  InputAdornment,
  FormHelperText,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';

import { escritorioService, Escritorio } from '../../services/escritorioService';

interface EscritorioSelectProps {
  value?: number | number[] | null;
  onChange: (value: number | number[] | null) => void;
  label?: string;
  size?: 'small' | 'medium';
  error?: boolean;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  multiple?: boolean;
  placeholder?: string;
  onlyActive?: boolean;
  allowClear?: boolean;
  fullWidth?: boolean;
  variant?: 'outlined' | 'filled' | 'standard';
  /** Quando true, desabilita o Portal do Menu - use em modais/dialogs fullscreen para o dropdown aparecer corretamente */
  disablePortal?: boolean;
  /** Quando true, exibe a opção "Todos" (valor vazio) */
  showTodosOption?: boolean;
}

const EscritorioSelect: React.FC<EscritorioSelectProps> = ({
  value,
  onChange,
  label = 'Escritório',
  size = 'medium',
  error = false,
  helperText,
  required = false,
  disabled = false,
  multiple = false,
  placeholder = 'Selecione um escritório',
  onlyActive = true,
  allowClear = false,
  fullWidth = true,
  variant = 'outlined',
  disablePortal = false,
  showTodosOption = false,
}) => {
  const [escritorios, setEscritorios] = useState<Escritorio[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);

  // Carregar escritórios
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadEscritorios();
    }, 300); // Debounce de 300ms

    return () => clearTimeout(timeoutId);
  }, [onlyActive, searchTerm]);

  const loadEscritorios = async () => {
    try {
      setLoading(true);
      setLoadError(null);

      const filters = {
        ativo: onlyActive ? 'true' as const : 'todos' as const,
        limit: 100, // Carregar bastantes escritórios
        search: searchTerm || undefined,
      };

      // Usar a nova rota otimizada para select
      const response = await escritorioService.getForSelect(filters);
      
      if (response.success) {
        setEscritorios(response.data || []);
      } else {
        throw new Error(response.message || 'Erro ao carregar escritórios');
      }
    } catch (err: any) {
      console.error('Erro ao carregar escritórios:', err);
      setLoadError(err.message || 'Erro ao carregar escritórios');
      setEscritorios([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (event: any) => {
    const selectedValue = event.target.value;
    
    if (multiple) {
      // Para seleção múltipla
      if (Array.isArray(selectedValue)) {
        onChange(selectedValue.length > 0 ? selectedValue : null);
      } else if (typeof selectedValue === 'number') {
        onChange([selectedValue]);
      } else {
        onChange(null);
      }
    } else {
      // Para seleção única
      onChange(selectedValue === '' ? null : selectedValue);
    }
  };

  const handleClear = (event: React.MouseEvent) => {
    event.stopPropagation();
    onChange(null);
  };

  const handleRemoveChip = (escritorioId: number, event: React.MouseEvent) => {
    event.stopPropagation();
    if (multiple && Array.isArray(value)) {
      const newValue = value.filter(id => id !== escritorioId);
      onChange(newValue.length > 0 ? newValue : null);
    }
  };

  const getSelectedEscritorios = (): Escritorio[] => {
    if (!value) return [];
    
    if (multiple) {
      // Garantir que seja sempre um array para seleção múltipla
      let arrayValue: number[] = [];
      if (Array.isArray(value)) {
        arrayValue = value;
      } else if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          arrayValue = Array.isArray(parsed) ? parsed : [];
        } catch {
          arrayValue = [];
        }
      } else if (typeof value === 'number') {
        arrayValue = [value];
      }
      
      return escritorios.filter(escritorio => arrayValue.includes(escritorio.id));
    } else if (!multiple && typeof value === 'number') {
      const escritorio = escritorios.find(e => e.id === value);
      return escritorio ? [escritorio] : [];
    }
    
    return [];
  };

  const renderValue = (selected: any) => {
    if (!selected || (Array.isArray(selected) && selected.length === 0)) {
      if (showTodosOption) return <em>Todos</em>;
      return <Box component="span" sx={{ color: 'text.disabled' }}>{placeholder}</Box>;
    }

    const selectedEscritorios = getSelectedEscritorios();
    
    if (multiple) {
      return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {selectedEscritorios.map((escritorio) => (
            <Chip
              key={escritorio.id}
              label={`${escritorio.identificacao} - ${escritorio.nome_fantasia}`}
              size="small"
              onDelete={(event) => handleRemoveChip(escritorio.id, event)}
              deleteIcon={<ClearIcon />}
              sx={{ 
                height: 24, 
                fontSize: '0.75rem',
                '& .MuiChip-deleteIcon': {
                  fontSize: '14px'
                  
                }
              }}
            />
          ))}
        </Box>
      );
    } else {
      const escritorio = selectedEscritorios[0];
      return escritorio ? `${escritorio.identificacao} - ${escritorio.nome_fantasia}` : '';
    }
  };

  const formatEscritorioLabel = (escritorio: Escritorio) => {
    let label = `${escritorio.identificacao} - ${escritorio.nome_fantasia}`;
    
    if (escritorio.is_default) {
      label += ' (Padrão)';
    }
    
    if (!escritorio.ativo) {
      label += ' (Inativo)';
    }
    
    return label;
  };

  const getTipoIcon = (tipo: string) => {
    return <BusinessIcon fontSize="small" />;
  };

  const getTipoColor = (tipo: string) => {
    switch (tipo) {
      case 'matriz':
        return 'primary';
      case 'filial':
        return 'secondary';
      default:
        return 'default';
    }
  };

  return (
    <FormControl 
      fullWidth={fullWidth} 
      size={size} 
      error={error} 
      variant={variant}
      disabled={disabled}
      required={required}
    >
      <InputLabel required={required} shrink>
        {label}
      </InputLabel>
      
      <Select
        value={(() => {
          if (multiple) {
            // Garantir que seja sempre um array para seleção múltipla
            if (Array.isArray(value)) {
              return value;
            } else if (typeof value === 'string') {
              try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
              } catch {
                return [];
              }
            } else if (typeof value === 'number') {
              return [value];
            }
            return [];
          } else {
            return value || '';
          }
        })()}
        size="small"
        label={label}
        onChange={handleChange}
        onOpen={() => setOpen(true)}
        onClose={() => {
          setOpen(false);
          setSearchTerm(''); // Limpar busca ao fechar
        }}
        displayEmpty
        multiple={multiple}
        renderValue={renderValue}
        MenuProps={{
          disablePortal,
          PaperProps: {
            style: {
              maxHeight: 400,
            },
            sx: disablePortal ? { zIndex: 99999 } : undefined,
          },
        }}
        startAdornment={
          <InputAdornment position="start">
            <BusinessIcon />
          </InputAdornment>
        }
        endAdornment={
          allowClear && value && (
            <InputAdornment position="end">
              <ClearIcon 
                onClick={handleClear}
                sx={{ 
                  cursor: 'pointer', 
                  fontSize: '20px',
                  '&:hover': { color: 'error.main' }
                }}
              />
            </InputAdornment>
          )
        }
      >
        {/* Campo de busca */}
        {open && (
          <Box sx={{ 
            p: 1, 
            borderBottom: 1, 
            borderColor: 'divider' 
          }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Buscar escritório..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: (theme) => theme.palette.mode === 'dark' 
                    ? 'rgba(255, 255, 255, 0.05)' 
                    : 'grey.50'
                }
              }}
            />
          </Box>
        )}

        {/* Loading */}
        {loading && (
          <MenuItem disabled>
            <Box display="flex" alignItems="center" gap={1}>
              <CircularProgress size={16} />
              <span>Carregando escritórios...</span>
            </Box>
          </MenuItem>
        )}

        {/* Erro */}
        {loadError && (
          <MenuItem disabled>
            <Alert severity="error" sx={{ width: '100%', py: 0.5 }}>
              {loadError}
            </Alert>
          </MenuItem>
        )}

        {/* Opção vazia (apenas para seleção única) */}
        {!multiple && !required && !loading && !loadError && (
          <MenuItem value="">
            <em>{showTodosOption ? 'Todos' : 'Nenhum escritório'}</em>
          </MenuItem>
        )}

        {/* Evita o warning do MUI de valor fora do range durante o loading */}
        {!multiple && value && typeof value === 'number' && !escritorios.some(e => e.id === value) && (
          <MenuItem value={value} sx={{ display: 'none' }}>
            {value}
          </MenuItem>
        )}

        {/* Lista de escritórios */}
        {!loading && !loadError && escritorios.map((escritorio) => (
          <MenuItem key={escritorio.id} value={escritorio.id}>
            <Box display="flex" alignItems="center" width="100%">
              {multiple && (
                <Checkbox
                  checked={(() => {
                    if (!value) return false;
                    if (Array.isArray(value)) {
                      return value.includes(escritorio.id);
                    } else if (typeof value === 'string') {
                      try {
                        const parsed = JSON.parse(value);
                        return Array.isArray(parsed) ? parsed.includes(escritorio.id) : false;
                      } catch {
                        return false;
                      }
                    } else if (typeof value === 'number') {
                      return value === escritorio.id;
                    }
                    return false;
                  })()}
                  size="small"
                />
              )}
              
              <Box display="flex" alignItems="center" gap={1} flex={1}>
                {getTipoIcon(escritorio.tipo)}
                
                <Box flex={1}>
                  <ListItemText
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <span>{escritorio.identificacao}</span>
                        {escritorio.is_default && (
                          <Chip 
                            label="Padrão" 
                            size="small" 
                            color="warning" 
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.6rem' }}
                          />
                        )}
                        <Chip
                          label={escritorio.tipo === 'matriz' ? 'Matriz' : 'Filial'}
                          size="small"
                          color={getTipoColor(escritorio.tipo) as any}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.6rem' }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <div>{escritorio.nome_fantasia}</div>
                        <Box 
                          component="div" 
                          sx={{ 
                            fontSize: '0.75rem', 
                            color: 'text.secondary' 
                          }}
                        >
                          {escritorio.cidade}/{escritorio.estado}
                        </Box>
                      </Box>
                    }
                    primaryTypographyProps={{ 
                      fontSize: '0.875rem',
                      fontWeight: escritorio.is_default ? 'bold' : 'normal'
                    }}
                    secondaryTypographyProps={{ 
                      fontSize: '0.75rem',
                      color: escritorio.ativo ? 'text.secondary' : 'error.main'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </MenuItem> 
        ))}

        {/* Nenhum resultado */}
        {!loading && !loadError && escritorios.length === 0 && (
          <MenuItem disabled>
            <em>Nenhum escritório encontrado</em>
          </MenuItem>
        )}
      </Select>

      {helperText && (
        <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
};

export default EscritorioSelect;
